import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
const authApi = axios.create({
  baseURL: API,
  withCredentials: true  // Send/receive cookies
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Cookie is sent automatically with withCredentials: true
      const response = await authApi.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        // Cookie is invalid or expired - clear user state
        setUser(null);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, twoFaCode = null) => {
    const payload = { email, password };
    if (twoFaCode) {
      payload.two_fa_code = twoFaCode;
    }
    
    const response = await authApi.post('/auth/login', payload);
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return response.data; // Return so caller can handle 2FA flow
    }
    
    // Cookie is set by server - just update user state
    if (response.data.user) {
      setUser(response.data.user);
    }
    
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await authApi.post('/auth/register', { email, password, name });
    // Cookie is set by server - just update user state
    setUser(response.data.user);
    return response.data;
  };

  const googleAuth = async (sessionId) => {
    const response = await authApi.post(`/auth/google?session_id=${sessionId}`);
    // Cookie is set by server - just update user state
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await authApi.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error.message);
      // Continue with local logout regardless
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleAuth, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;